import { createElement as h, useEffect, useRef, useState } from 'react';
import type { ItineraryFacilityDetail } from '../api/itinerary.ts';
import { shortDay } from '../itineraryPrint.ts';

/**
 * A building's panel on the itinerary map (Arthur, 22 October, A-#41). A pin's click opens it and NEVER adds: it
 * shows the building's name and address and the clients due there (room, visit type), read through the one audited
 * per-building call (141) on that click, then Add to itinerary and Close; once added, "Stop N", Remove and Close.
 * Hovering a pin shows only its name and the counts.
 *
 * Residents (A-#54): under the due, a line "M up to date" opens a compact list of the other residents, name and room
 * beside the last visit; and before Add, a toggle, "See everyone here" (on) or "Only clients due", which is the stop's
 * own and can be changed after adding. Written with createElement, not JSX, so web/test/itinerary-panel.test.ts can
 * render it under node.
 */

export type PanelState =
  | { kind: 'loading'; id: string; title: string }
  | { kind: 'failed'; id: string; title: string; message: string }
  | { kind: 'ready'; id: string; title: string; detail: ItineraryFacilityDetail };

const btn = 'min-h-[44px] min-w-[44px] rounded-lg border border-line bg-surface px-3 font-semibold text-neutral-900 disabled:text-neutral-500';
const primary = 'min-h-[44px] rounded-lg bg-brand px-4 font-semibold text-on-brand disabled:bg-neutral-400';

export function addressLine(f: ItineraryFacilityDetail['facility']): string {
  const town = [f.city, [f.state, f.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [f.street_address, town].filter(Boolean).join(', ') || 'No address on record';
}

/** A stop's choice of who is on its checklist: two buttons, one pressed. Shared by the panel and the itinerary. */
export function EveryoneToggle({ name, everyone, onChange }: { name: string; everyone: boolean; onChange: (everyone: boolean) => void }) {
  return h('div', { role: 'group', 'aria-label': 'Who is on the checklist', 'data-everyone': name, className: 'flex' },
    ...([[true, 'See everyone here'], [false, 'Only clients due']] as const).map(([v, label]) => h('button', {
      key: label, type: 'button', 'aria-pressed': everyone === v, onClick: () => onChange(v),
      className: `min-h-[44px] border border-line px-3 text-sm font-semibold first:rounded-l-lg last:rounded-r-lg ${
        everyone === v ? 'bg-brand text-on-brand' : 'bg-surface text-neutral-900'}`,
    }, label)));
}

export function BuildingPanel({ state, stop, busy, everyone, onEveryone, onAdd, onRemove, onClose }: {
  state: PanelState;
  /** The building's stop number when it is in the itinerary, else null. */
  stop: number | null;
  busy: boolean;
  /** The toggle: the stop's own once added, the panel's choice before. */
  everyone: boolean;
  onEveryone: (everyone: boolean) => void;
  onAdd: (detail: ItineraryFacilityDetail) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [showCurrent, setShowCurrent] = useState(false);
  // Focus follows the click into the panel, so a keyboard reader lands on what opened, and comes back to it when Add
  // or Remove replaces the button just pressed (else it falls to the page); Escape closes it.
  useEffect(() => { heading.current?.focus(); }, [state.id, stop]);
  useEffect(() => { setShowCurrent(false); }, [state.id]);

  const detail = state.kind === 'ready' ? state.detail : null;
  const current = detail?.up_to_date ?? [];
  const body = state.kind === 'loading'
    ? h('p', { role: 'status', className: 'text-neutral-700' }, 'Loading the clients due here…')
    : state.kind === 'failed'
      ? h('p', { role: 'alert', className: 'font-semibold text-critical-edge' }, state.message)
      : h('div', null,
          h('p', { className: 'text-sm text-neutral-700' }, addressLine(detail!.facility)),
          detail!.clients.length === 0
            ? h('p', { className: 'mt-2 text-neutral-700' }, 'No client is due here by this date.')
            : h('ol', { 'aria-label': 'Clients due', className: 'mt-2 divide-y divide-line' },
                detail!.clients.map((c) => h('li', { key: c.client_id, className: 'py-1.5' },
                  h('span', { className: 'text-neutral-900' }, c.name),
                  h('span', { className: 'block text-sm text-neutral-700' },
                    `${c.room ? `Room ${c.room}` : 'No room on record'} · ${c.visit_type}${c.overdue ? ', overdue' : ''}`)))),
          detail!.away > 0
            ? h('p', { className: 'mt-2 text-sm text-neutral-700' },
                `${detail!.away} more ${detail!.away === 1 ? 'client is' : 'clients are'} due but away on a temporary stay, so not listed.`)
            : null,
          h('button', {
            type: 'button', 'aria-expanded': showCurrent, 'aria-controls': `up-to-date-${state.id}`, disabled: current.length === 0,
            onClick: () => setShowCurrent((v) => !v),
            className: 'mt-2 flex min-h-[44px] w-full items-center justify-between rounded-lg border border-line bg-surface px-3 text-left font-semibold text-neutral-900 disabled:text-neutral-700',
          }, `${current.length} up to date`, current.length > 0 ? h('span', { 'aria-hidden': true }, showCurrent ? '▴' : '▾') : null),
          showCurrent && current.length > 0
            ? h('table', { id: `up-to-date-${state.id}`, className: 'mt-1 w-full text-sm' },
                h('thead', null, h('tr', { className: 'text-left text-neutral-700' },
                  h('th', { className: 'py-1 font-semibold' }, 'Name and room'), h('th', { className: 'py-1 font-semibold' }, 'Last visit'))),
                h('tbody', { className: 'divide-y divide-line' },
                  current.map((c) => h('tr', { key: c.client_id },
                    h('td', { className: 'py-1 pr-2 text-neutral-900' }, c.name,
                      h('span', { className: 'block text-neutral-700' }, c.room ? `Room ${c.room}` : 'No room on record')),
                    h('td', { className: 'py-1 align-top text-neutral-900' }, c.last_visit ? shortDay(c.last_visit) : 'None on record')))))
            : null,
          detail!.away_up_to_date > 0
            ? h('p', { className: 'mt-1 text-sm text-neutral-700' },
                `${detail!.away_up_to_date} more up to date ${detail!.away_up_to_date === 1 ? 'is' : 'are'} away on a temporary stay, so not listed.`)
            : null);

  const actions = stop !== null
    ? [h('span', { key: 's', className: 'inline-flex min-h-[44px] items-center rounded-lg bg-brand px-3 font-semibold text-on-brand' }, `Stop ${stop}`),
       h('button', { key: 'r', type: 'button', className: btn, onClick: onRemove }, 'Remove')]
    : [h('button', { key: 'a', type: 'button', className: primary, disabled: !detail || busy, onClick: () => { if (detail) onAdd(detail); } }, 'Add to itinerary')];

  return h('section', {
    'aria-label': `Building: ${state.title}`, 'data-building-panel': state.id,
    onKeyDown: (e: { key: string }) => { if (e.key === 'Escape') onClose(); },
    className: 'flex max-h-full w-[22rem] max-w-full flex-col rounded-lg border border-line bg-panel shadow-lg',
  },
    h('h2', { ref: heading, tabIndex: -1, className: 'rounded-t-lg bg-brand px-3 py-2 font-semibold text-on-brand' }, state.title),
    h('div', { className: 'min-h-0 flex-1 overflow-y-auto px-3 py-2' }, body),
    h('div', { className: 'border-t border-line px-3 pt-2' },
      h(EveryoneToggle, { name: `panel-${state.id}`, everyone, onChange: onEveryone })),
    h('div', { className: 'flex flex-wrap items-center gap-2 px-3 py-2' },
      ...actions,
      h('button', { key: 'c', type: 'button', className: `${btn} ml-auto`, onClick: onClose }, 'Close')));
}
